import { XMarkIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { areasService, OrganizationArea } from '../services/areas';
import {
  MasterPlanTask,
  masterPlanTaskService,
  // TIME_PERIODS removed - use due_date instead
} from '../services/masterPlan';
import { getAuthHeaders } from '../utils/auth';

import { API_URL } from '../constants/urls';

interface TaskModalProps {
  task: MasterPlanTask | null;
  organizationId: string;
  onClose: () => void;
  onSave: (task: MasterPlanTask) => void;
  segments: any[];
  propositions: any[];
  groups: any[];
  positions?: any[];
  companies?: any[];
  isAdmin: boolean;
  initialValues?: {
    // For pre-populating fields in create mode
    context_type?: string;
    context_object_id?: string;
    okr_id?: string | null;
    key_result_id?: string;
    area_id?: string;
    assigned_to_id?: string;
  };
}

const TaskModal: React.FC<TaskModalProps> = ({
  task,
  organizationId,
  onClose,
  onSave,
  segments,
  propositions,
  groups,
  positions = [],
  companies = [],
  isAdmin,
  initialValues,
}) => {
  const [formData, setFormData] = useState({
    title: task?.title || '',
    description: task?.description || '',
    area_id: task?.area_id || initialValues?.area_id || '',
    assigned_to_id: task?.assigned_to_id || initialValues?.assigned_to_id || '',
    status: task?.status || 'Not Started',
    priority: task?.priority || 'Medium',
    // time_period removed - use due_date instead
    due_date: task?.due_date ? task.due_date.split('T')[0] : '',
    okr_id: task?.okr_id || initialValues?.okr_id || '',
    key_result_id: task?.key_result_id || initialValues?.key_result_id || '',
    context_type: task?.context_type || initialValues?.context_type || '',
    context_object_id:
      task?.context_object_id || initialValues?.context_object_id || '',
    notes: task?.notes || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [areas, setAreas] = useState<OrganizationArea[]>([]);
  const [keyResults, setKeyResults] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [okrs, setOkrs] = useState<any[]>([]);

  useEffect(() => {
    if (formData.okr_id) {
      loadKeyResults(formData.okr_id);
    }
  }, [formData.okr_id]);

  useEffect(() => {
    loadAreas();
    loadUsers();
    loadOkrs();
  }, [organizationId]);

  const loadAreas = async () => {
    try {
      const areasData = await areasService.list(organizationId, false);
      setAreas(areasData);
    } catch (err) {
      // console.error removed
    }
  };

  const loadUsers = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/organizations/${organizationId}/members`,
        { headers: getAuthHeaders() },
      );
      setUsers(response.data || []);
    } catch (err) {
      // console.error removed
    }
  };

  const loadOkrs = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/okrs?organization_id=${organizationId}`,
        { headers: getAuthHeaders() },
      );
      const allOkrs = response.data || [];

      // Filter to active/draft OKRs for selection
      const selectableOkrs = allOkrs.filter(
        (okr: any) => okr.status === 'active' || okr.status === 'draft',
      );

      // If editing a task with a completed/cancelled OKR, include it as disabled option
      if (
        task?.okr_id &&
        !selectableOkrs.find((okr: any) => okr.id === task.okr_id)
      ) {
        const currentOkr = allOkrs.find((okr: any) => okr.id === task.okr_id);
        if (currentOkr) {
          selectableOkrs.push({
            ...currentOkr,
            disabled: true,
            displaySuffix: ` (${currentOkr.status === 'completed' ? 'Completed' : 'Cancelled'})`,
          });
        }
      }

      setOkrs(selectableOkrs);
    } catch (err) {
      // console.error removed
    }
  };

  const loadKeyResults = async (okrId: string) => {
    try {
      // Key Results are included in the OKR object
      const response = await axios.get(`${API_URL}/okrs/${okrId}`, {
        params: { organization_id: organizationId },
        headers: getAuthHeaders(),
      });
      setKeyResults(response.data.key_results || []);
    } catch (err) {
      console.error('Failed to load key results:', err);
      setKeyResults([]);
    }
  };

  const getContextObjects = () => {
    switch (formData.context_type) {
      case 'segment':
        return segments;
      case 'proposition':
        return propositions;
      case 'group':
        return groups;
      case 'organization':
        return [{ id: organizationId, name: 'Organization' }];
      case 'position':
        // Use the positions array passed as a prop
        return positions.map((position: any) => ({
          id: position.id,
          name: `${position.proposition_name} × ${position.segment_name}`,
        }));
      case 'company':
        return companies;
      default:
        return [];
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const data: any = {
        title: formData.title,
        description: formData.description || undefined,
        area_id: formData.area_id || undefined,
        assigned_to_id: formData.assigned_to_id || undefined,
        status: formData.status,
        priority: formData.priority,
        // time_period removed - use due_date instead
        due_date: formData.due_date || undefined,
        notes: formData.notes || undefined,
      };

      // Handle OKR fields - explicitly set to null when empty
      if (formData.okr_id) {
        data.okr_id = formData.okr_id;
      } else {
        // Explicitly set to null to clear the OKR alignment
        data.okr_id = null;
      }
      if (formData.key_result_id) {
        data.key_result_id = formData.key_result_id;
      } else if (data.okr_id === null) {
        // Also clear key result if OKR is cleared
        data.key_result_id = null;
      }

      // Only add context fields if type is selected
      if (formData.context_type) {
        data.context_type = formData.context_type;

        // For organization context, use the organizationId
        if (formData.context_type === 'organization') {
          data.context_object_id = organizationId;
        } else if (formData.context_object_id) {
          // For other contexts, only add if object is selected
          data.context_object_id = formData.context_object_id;
        }
      }

      let result;
      if (task) {
        // Update existing task
        result = await masterPlanTaskService.update(
          organizationId,
          task.id,
          data,
        );
      } else {
        // Create new task
        console.log('Creating task with data:', data);
        result = await masterPlanTaskService.create(organizationId, data);
        console.log('Created task result:', result);
      }

      onSave(result);
    } catch (err: any) {
      // console.error removed
      const errorDetail = err.response?.data?.detail;
      if (typeof errorDetail === 'string') {
        setError(errorDetail);
      } else if (Array.isArray(errorDetail)) {
        // Handle validation errors array
        setError(
          errorDetail
            .map((e: any) => e.msg || e.message || JSON.stringify(e))
            .join(', '),
        );
      } else if (errorDetail && typeof errorDetail === 'object') {
        // Handle single validation error object
        setError(
          errorDetail.msg || errorDetail.message || JSON.stringify(errorDetail),
        );
      } else {
        setError('Failed to save task');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500/75 transition-opacity"
          onClick={onClose}
        />

        <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {task ? 'Edit Task' : 'Create Task'}
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Title *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    rows={3}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Context Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Context Level
                    </label>
                    <select
                      value={formData.context_type}
                      onChange={(e) => {
                        const newType = e.target.value;
                        setFormData({
                          ...formData,
                          context_type: newType,
                          context_object_id:
                            newType === 'organization'
                              ? organizationId
                              : // Only reset if the type actually changed
                                newType !== formData.context_type
                                ? ''
                                : formData.context_object_id,
                        });
                      }}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select context...</option>
                      {isAdmin && (
                        <option value="organization">Organization</option>
                      )}
                      <option value="group">Group</option>
                      <option value="segment">Segment</option>
                      <option value="proposition">Proposition</option>
                      <option value="position">Position</option>
                      <option value="company">Company</option>
                    </select>
                  </div>

                  {/* Context Object */}
                  {formData.context_type && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        {formData.context_type === 'organization'
                          ? 'Organization'
                          : `Select ${formData.context_type}`}
                      </label>
                      {formData.context_type === 'organization' ? (
                        <input
                          type="text"
                          value="Organization"
                          disabled
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                        />
                      ) : (
                        <select
                          value={formData.context_object_id}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              context_object_id: e.target.value,
                            })
                          }
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">
                            Select {formData.context_type}...
                          </option>
                          {getContextObjects().map((obj: any) => (
                            <option key={obj.id} value={obj.id}>
                              {obj.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          status: e.target.value as any,
                        })
                      }
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="Not Started">Not Started</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Complete">Complete</option>
                      <option value="Blocked">Blocked</option>
                    </select>
                  </div>

                  {/* Priority */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Priority
                    </label>
                    <select
                      value={formData.priority}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          priority: e.target.value as any,
                        })
                      }
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="High">High</option>
                      <option value="Medium">Medium</option>
                      <option value="Low">Low</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Area */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Area
                    </label>
                    <select
                      value={formData.area_id}
                      onChange={(e) =>
                        setFormData({ ...formData, area_id: e.target.value })
                      }
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select area...</option>
                      {areas.map((area) => (
                        <option key={area.id} value={area.id}>
                          {area.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Assigned To */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Assigned To
                    </label>
                    <select
                      value={formData.assigned_to_id}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          assigned_to_id: e.target.value,
                        })
                      }
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Unassigned</option>
                      {users.map((user) => (
                        <option key={user.user_id} value={user.user_id}>
                          {user.full_name || user.user_name || user.user_email}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Due Date - time_period removed, use due_date instead */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Due Date
                  </label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) =>
                      setFormData({ ...formData, due_date: e.target.value })
                    }
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* OKR Alignment */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Aligned to OKR
                  </label>
                  <select
                    value={formData.okr_id}
                    onChange={(e) => {
                      const okrId = e.target.value;
                      setFormData({
                        ...formData,
                        okr_id: okrId,
                        key_result_id: '',
                      });
                      if (okrId) {
                        loadKeyResults(okrId);
                      } else {
                        setKeyResults([]);
                      }
                    }}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">No OKR alignment</option>
                    {okrs.map((okr) => (
                      <option
                        key={okr.id}
                        value={okr.id}
                        disabled={okr.disabled}
                        className={okr.disabled ? 'text-gray-400 italic' : ''}
                      >
                        {okr.formatted_reference
                          ? `${okr.formatted_reference} - `
                          : ''}
                        {okr.objective}
                        {okr.is_corporate ? ' [Corporate]' : ''}
                        {okr.displaySuffix || ` (${okr.status})`}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Key Result (shown when OKR is selected) */}
                {formData.okr_id && keyResults.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Key Result
                    </label>
                    <select
                      value={formData.key_result_id}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          key_result_id: e.target.value,
                        })
                      }
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">No specific key result</option>
                      {keyResults.map((kr) => (
                        <option key={kr.id} value={kr.id}>
                          {kr.description} (Target: {kr.target_value})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                    rows={2}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto sm:ml-3 inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 sm:mt-0 w-full sm:w-auto inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TaskModal;
